import * as React              from 'react';
import { useTranslation }      from 'react-i18next';
import { Navigation }          from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useCatalog } from '../../providers/catalog';

import NextSliderArrow     from '../icons/NextSliderArrow';
import PrevSliderArrow     from '../icons/PrevSliderArrow';
import { RenderBasicTabs } from '../tabs/RenderBasicTabs';

import CatalogItem from './CatalogItem';

const PopularSlider = function(props) {
  const { t } = useTranslation();

  const {
          isInterior,
          installationTypeId,
          label,
          popularModels,
        } = props;

  const catalog = useCatalog();

  const nextArrow = React.useRef(null);
  const prevArrow = React.useRef(null);

  const [tab, setTab] = React.useState(0);

  const installationType = React.useMemo(() => {
    return installationTypeId ?? catalog.filter?.selectedValues?.it;
  }, [catalog.filter.selectedValues?.it, installationTypeId]);
  const tabs             = React.useMemo(() => {
    const tabs = [];

    const models = Object
      .values(popularModels ?? {})
      .filter((model) => {
        return !installationType || model.installationTypeId === installationType;
      });

    for (let model of models) {
      if (Array.isArray(model?.items) && model.items.length > 0) {
        tabs.push(model);
      }
    }

    return tabs.sort((a, b) => {
      return b.installationTypeId - a.installationTypeId;
    });
  }, [installationType, popularModels]);

  const changeTab = React.useCallback((activeTab) => {
    if (tab !== activeTab) {
      setTab(activeTab);
    }
  }, [tab]);

  if (!tabs.length) {
    return null;
  }

  return (
    <div className={'popularModels'}>
      <div className={'container'}>
        <RenderBasicTabs
          active={tab}
          changeTab={changeTab}
          nav={tabs.length > 1
            ? tabs.map(({ label }) => ({ label }))
            : []
          }
          title={label ?? t('client_popular_models')}
        />
      </div>
      <div className={'slider'}>
        <Swiper
          breakpoints={{
            425: {
              slidesPerView: 2,
            },
          }}
          navigation
          // navigation={{ nextEl: nextArrow.current, prevEl: prevArrow.current }} reads the
          // refs during render, before React has committed the DOM and attached them — Swiper
          // mounts with nextEl/prevEl baked in as null and never re-reads the refs afterwards,
          // so the arrows render but are never wired to the instance. onBeforeInit looked like
          // the fix (it's the documented one), but in this swiper/react version it fires from
          // inside the Swiper constructor during render — before commit, so the refs are still
          // null there too. onSwiper is the one callback that's actually guaranteed to run after
          // mount (same pattern already used for the pagination module in
          // new-components/offers-slider/offers-slider.tsx): by then the refs are populated, so
          // re-pointing swiper.params.navigation and redoing navigation's own
          // destroy/init/update — it already bound its click handlers to nothing on the first,
          // premature init — actually wires the clicks up.
          onSwiper={(swiper) => {
            // Even onSwiper — swiper/react's own "definitely after mount" callback — fires
            // one tick before nextArrow.current/prevArrow.current (siblings of <Swiper>, not
            // its children) are actually attached: this page hits a severe hydration mismatch
            // (React discards the server-rendered tree and remounts client-side, logged as
            // repeated error #418/#423), which appears to split what would normally be a single
            // commit into more than one. Confirmed empirically — the refs are still null here
            // but populated by the very next setTimeout(0). Deferring the rewiring by a tick
            // sidesteps the ordering without needing to fix the hydration issue itself.
            setTimeout(() => {
              if (swiper.destroyed) {
                return;
              }

              // The premature first init() ran with navigation.nextEl/prevEl still unset, so
              // Swiper's own createElementIfNotDefined() auto-created its default
              // .swiper-button-next/.swiper-button-prev pair and appended them to the DOM.
              // navigation.destroy() only strips their click listener, it never removes the
              // elements — left alone, they'd sit next to our real arrows as dead duplicates.
              swiper.el
                .querySelectorAll('.swiper-button-next, .swiper-button-prev')
                .forEach((el) => el.remove());

              swiper.params.navigation.nextEl = nextArrow.current;
              swiper.params.navigation.prevEl = prevArrow.current;
              swiper.navigation?.destroy();
              swiper.navigation?.init();
              swiper.navigation?.update();
            }, 0);
          }}
          modules={[Navigation]}
          loop={false}
          slideToClickedSlide
          slidesPerView={1}
          speed={5e2}
        >
          {Object
            .values(tabs[tab].items)
            .map((model, m) => {
              const currentOffer   = Object.values(model)
                .find((offer) => {
                  return true === offer.common.current;
                });
              const currentOfferId = currentOffer?.common.current ?? Object.keys(model)[0];
              const offers         = currentOfferId
                ? { [currentOfferId]: model[currentOfferId] }
                : model;

              return (
                <SwiperSlide key={m}>
                  <CatalogItem
                    currentOfferId={currentOfferId}
                    isInterior={isInterior}
                    offers={offers}
                  />
                </SwiperSlide>
              );
            })
          }
        </Swiper>
        <PrevSliderArrow ref={prevArrow} />
        <NextSliderArrow ref={nextArrow} />
      </div>
    </div>
  );
};

export default PopularSlider;